// ----------------------------------------------------------------------------
// Members: everyone in the church with their ROLE (Admin, Member, Singer,
// Musician) and teams. Positions are not listed here; they are edited in the
// member's modal (once the API has a field for them — see redesignFlags).
// Inviting happens with invite codes only, on the Invite codes page.
// ----------------------------------------------------------------------------

import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useAuth } from '@app/auth/AuthContext';
import { useChurch } from '@admin/shared/contexts/ChurchContext';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import { membershipsApi } from '@app/api/organization';
import { ChurchMembershipState } from '@app/types';
import { Toast } from '@app/components/Notifications';
import { Avatar, Button, Chip, ConfirmDialog, EmptyState, ErrorState, Field, Input, Modal, PageHeader, Select, SkeletonRows, StatusPill, cx } from '@app/components/ui';
import { useMediaQuery, BP } from '@app/hooks/useMediaQuery';
import { emitMembershipChanged } from '@app/utils/membershipEvents';
import { logger } from '@app/utils/logger';
import { apiErrorMessage } from '@app/lib/apiErrors';
import { redesignFlags } from '@app/featureFlags';
import { useChurchPeople, type ChurchPerson } from '../../hooks/useChurchPeople';
import { POSITIONS } from '../../components/roster/TeamSlots';

export const ROLES = ['Admin', 'Member', 'Singer', 'Musician'] as const;
const ROLE_SUB: Record<string, string> = {
  Admin: 'Full access to every team, roster and billing',
  Member: 'Part of the church. Can be scheduled for any position',
  Singer: 'Sings on the team',
  Musician: 'Plays an instrument on the team',
};
const COLS = 'minmax(0,2fr) minmax(0,1.8fr) 100px minmax(0,1.4fr) 84px 64px';

const joined = (iso: string) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '–');

export const Members: React.FC = () => {
  const { user } = useAuth();
  const { church } = useChurch();
  const { groups } = useGroup();
  const churchId = user?.churchId;
  const wide = useMediaQuery(BP.membersTable);
  const { people, setPeople, loading, error, reload } = useChurchPeople(churchId);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<ChurchPerson | null>(null);
  const [toast, setToast] = useState<{ m: string; t: 'success' | 'error' } | null>(null);

  const teamName = (id: string) => groups.find((g) => g.id === id)?.name;
  const active = useMemo(() => people.filter((p) => p.churchState === ChurchMembershipState.Active), [people]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return active.filter((p) => !q || p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q) || p.role.toLowerCase().includes(q));
  }, [active, query]);

  const rolePill = (role: string) => <StatusPill tone={role === 'Admin' ? 'acc' : 'muted'} className={role === 'Admin' ? '' : '!text-t2'}>{role}</StatusPill>;
  const teamsLabel = (p: ChurchPerson) => {
    const on = p.groupIds.map(teamName).filter(Boolean).join(', ');
    if (on) return on;
    const asked = p.pendingGroupId ? teamName(p.pendingGroupId) : undefined;
    return asked ? `Not on a team · asked for ${asked}` : 'Not on a team';
  };

  return (
    <div>
      {toast && <Toast message={toast.m} type={toast.t} onClose={() => setToast(null)} />}
      <PageHeader
        eyebrow={`${active.length} ${active.length === 1 ? 'person' : 'people'} in ${church?.name ?? 'your church'}`}
        title="Members"
        actions={
          <div className="w-[280px] max-w-full">
            <Input
              height={40}
              pill
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email or role"
              aria-label="Search members"
              leading={<Search size={15} />}
              className="!bg-card !text-[14px]"
            />
          </div>
        }
      />

      {error ? (
        <ErrorState title="Couldn't load members" body="Check your connection and try again." onRetry={reload} retrying={loading} />
      ) : loading ? (
        <SkeletonRows rows={6} />
      ) : active.length === 0 ? (
        <EmptyState title="No members yet" body="Share an invite code. People who join appear here once you approve them." />
      ) : shown.length === 0 ? (
        <div className="py-[60px] text-center text-t4 text-[14px]">No one matches that search.</div>
      ) : wide ? (
        <div className="rounded-[22px] border border-line bg-card overflow-hidden">
          <div role="table" aria-label="Members">
            <div role="row" className="grid gap-3 px-5 py-3.5 wp-eyebrow border-b border-line" style={{ gridTemplateColumns: COLS }}>
              <span role="columnheader">Name</span>
              <span role="columnheader">Email</span>
              <span role="columnheader">Role</span>
              <span role="columnheader">Team</span>
              <span role="columnheader">Joined</span>
              <span role="columnheader" className="sr-only">
                Edit
              </span>
            </div>
            {shown.map((p) => (
              <div key={p.userId} role="row" className="grid gap-3 items-center px-5 py-[11px] border-b border-line last:border-0 text-[14px] hover:bg-fill" style={{ gridTemplateColumns: COLS }}>
                <span role="cell" className="flex items-center gap-2.5 min-w-0">
                  <Avatar name={p.name} size={32} />
                  <span className="font-semibold truncate">{p.name}</span>
                </span>
                <span role="cell" className="text-t3 text-[13.5px] truncate">
                  {p.email}
                </span>
                <span role="cell">{rolePill(p.role)}</span>
                <span role="cell" className={cx('text-[13px] truncate', p.groupIds.length ? 'text-t3' : 'text-t5')}>
                  {teamsLabel(p)}
                </span>
                <span role="cell" className="text-t4 text-[13px]">
                  {joined(p.joinedAt)}
                </span>
                <span role="cell" className="justify-self-end">
                  <Button variant="ghost" size="sm" className="!h-[30px] !px-3" onClick={() => setEditing(p)} aria-label={`Edit ${p.name}`}>
                    Edit
                  </Button>
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <ul className="rounded-[22px] border border-line bg-card" aria-label="Members">
          {shown.map((p) => (
            <li key={p.userId} className="flex items-center gap-3 px-4 py-3 border-b border-line last:border-0">
              <Avatar name={p.name} size={38} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[14.5px] font-semibold truncate">{p.name}</span>
                  {rolePill(p.role)}
                </div>
                <div className="text-[12.5px] text-t4 mt-0.5 truncate">{p.email}</div>
                <div className={cx('text-[12.5px] mt-px truncate', p.groupIds.length ? 'text-t3' : 'text-t5')}>{teamsLabel(p)}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setEditing(p)} aria-label={`Edit ${p.name}`}>
                Edit
              </Button>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <EditMemberModal
          person={editing}
          onClose={() => setEditing(null)}
          onSaved={(next, msg) => {
            setPeople((prev) => (next ? prev.map((x) => (x.userId === next.userId ? next : x)) : prev.filter((x) => x.userId !== editing.userId)));
            setEditing(null);
            setToast({ m: msg, t: 'success' });
          }}
          onError={(m) => setToast({ m, t: 'error' })}
        />
      )}
    </div>
  );
};

function EditMemberModal({
  person,
  onClose,
  onSaved,
  onError,
}: {
  person: ChurchPerson;
  onClose: () => void;
  onSaved: (next: ChurchPerson | null, message: string) => void;
  onError: (message: string) => void;
}) {
  const { user } = useAuth();
  const { groups } = useGroup();
  const [role, setRole] = useState(person.role);
  // One team per person: a membership carries a single team assignment, and
  // adding someone to a team moves them off their current one.
  const savedTeam = person.groupIds[0] ?? '';
  const askedFor = person.pendingGroupId ? groups.find((g) => g.id === person.pendingGroupId)?.name : undefined;
  const [team, setTeam] = useState(savedTeam);
  const [positions, setPositions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const roleOptions = [...ROLES, ...(ROLES.includes(person.role as (typeof ROLES)[number]) ? [] : [person.role])].map((r) => ({
    value: r,
    label: r,
    sub: ROLE_SUB[r],
  }));

  const save = async () => {
    if (!user) return;
    setSaving(true);
    try {
      if (role !== person.role) {
        await membershipsApi.updateMembership(person.membershipId, {
          role,
          function: person.function || undefined,
          churchState: person.churchState,
          updatedBy: user.id,
        });
      }
      if (team !== savedTeam) {
        if (team) {
          // Moves them: the server takes them off their old team in the same call.
          await membershipsApi.addGroupMember({ userId: person.userId, churchId: user.churchId!, groupId: team, role: 'Member', addedBy: user.id });
        } else {
          await membershipsApi.removeFromGroup(savedTeam, person.userId);
        }
        emitMembershipChanged();
      }
      onSaved({ ...person, role, groupIds: team ? [team] : [] }, `${person.name} updated`);
    } catch (err: any) {
      logger.error('Failed to update member', err);
      onError(apiErrorMessage(err, 'Could not save changes'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!user?.churchId) return;
    try {
      await membershipsApi.removeFromChurch(user.churchId, person.userId);
      emitMembershipChanged();
      onSaved(null, `${person.name} removed`);
    } catch (err: any) {
      onError(err?.response?.data?.error || 'Could not remove member');
    }
  };

  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  return (
    <>
      <Modal open onClose={onClose} aria-label={`Edit ${person.name}`} showClose={false}>
        <div className="flex flex-col gap-[18px]">
          <div className="flex items-center gap-3.5">
            <Avatar name={person.name} size={48} />
            <div className="min-w-0">
              <h2 className="text-[20px] font-semibold tracking-[-0.01em] truncate">{person.name}</h2>
              <div className="text-[13px] text-t4 truncate">{person.email}</div>
            </div>
          </div>
          <Field label="Role" htmlFor="member-role">
            <Select id="member-role" value={role} onChange={setRole} options={roleOptions} />
          </Field>
          {redesignFlags.memberPositions && (
            <div>
              <span className="wp-label !mb-1">Positions</span>
              <p className="text-[12.5px] text-t4 mb-2.5">What they can be scheduled for on a roster.</p>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Positions">
                {POSITIONS.map((p) => (
                  <Chip key={p} on={positions.includes(p)} onClick={() => setPositions(toggle(positions, p))}>
                    {p}
                  </Chip>
                ))}
              </div>
            </div>
          )}
          <Field
            label="Worship team"
            htmlFor="member-team"
            hint={
              askedFor && !savedTeam
                ? `They asked to join ${askedFor} with an invite code, but it was full. Raise its size limit on Teams, then pick it here.`
                : 'Each person is on one team. Picking another moves them.'
            }
          >
            <Select
              id="member-team"
              value={team}
              onChange={setTeam}
              options={[
                { value: '', label: 'Not on a team' },
                ...groups.map((g) => ({
                  value: g.id,
                  label: g.name,
                  sub: g.maxCapacity ? `Limit ${g.maxCapacity}` : undefined,
                })),
              ]}
            />
          </Field>
          <div className="flex flex-wrap gap-2.5 mt-1">
            {person.userId !== user?.id && (
              <Button variant="plain" size="lg" className="!text-acc" onClick={() => setConfirmRemove(true)}>
                Remove
              </Button>
            )}
            <span className="flex-1" />
            <Button variant="ghost" shape="form" size="lg" onClick={onClose}>
              Cancel
            </Button>
            <Button shape="form" size="lg" loading={saving} onClick={save}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
      <ConfirmDialog
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={remove}
        type="danger"
        title={`Remove ${person.name}?`}
        message="They lose access to your church and come off every upcoming roster. Past rosters keep their record."
        confirmText="Remove from church"
      />
    </>
  );
}
